/**
 * Live-campaign lookups for posts.
 *
 * One React Query cache entry per client holds all live campaigns
 * (CREATING/ACTIVE/PAUSED). Per-post hooks read from that cache — no
 * per-card network call. Mutations (pause/resume/cancel/create)
 * invalidate the key to refetch.
 *
 * A post can have up to TWO live campaigns (one FACEBOOK + one
 * INSTAGRAM), so `useLiveCampaignsForPost` returns an array.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { adsService } from "../services/adsService";
import type { Campaign, MetaPlatform } from "../types";

export const adsQueryKeys = {
  liveCampaigns: (clientId: string) => ["ads", "live-campaigns", clientId] as const,
  pastCampaigns: (clientId: string) => ["ads", "past-campaigns", clientId] as const,
};

export function useLiveCampaigns(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId
      ? adsQueryKeys.liveCampaigns(clientId)
      : ["ads", "live-campaigns", "none"],
    queryFn: async (): Promise<Campaign[]> => {
      if (!clientId) return [];
      return adsService.listCampaigns(clientId, [
        "CREATING",
        "ACTIVE",
        "PAUSED",
      ]);
    },
    enabled: !!clientId,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/** Finished campaigns (COMPLETED + CANCELLED), ordered most-recently
 *  finished first. FAILED is excluded — those never reached Meta and
 *  carry zero metrics that would mislead in a history view. Sort key
 *  is end_date desc with updated_at as tiebreaker for campaigns that
 *  never picked up an end_date. */
export function usePastCampaigns(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId
      ? adsQueryKeys.pastCampaigns(clientId)
      : ["ads", "past-campaigns", "none"],
    queryFn: async (): Promise<Campaign[]> => {
      if (!clientId) return [];
      const list = await adsService.listCampaigns(clientId, [
        "COMPLETED",
        "CANCELLED",
      ]);
      return [...list].sort((a, b) => {
        const ka = a.endDate ?? a.updatedAt ?? "";
        const kb = b.endDate ?? b.updatedAt ?? "";
        return kb.localeCompare(ka);
      });
    },
    enabled: !!clientId,
    staleTime: 60_000,
  });
}

export function useLiveCampaignsForPost(
  clientId: string | undefined,
  postId: string | undefined
): Campaign[] {
  const { data } = useLiveCampaigns(clientId);
  return useMemo(() => {
    if (!postId || !data) return [];
    return data.filter((c) => c.postId === postId);
  }, [data, postId]);
}

export function useLiveCampaignForPostPlatform(
  clientId: string | undefined,
  postId: string | undefined,
  platform: MetaPlatform | undefined
): Campaign | null {
  const list = useLiveCampaignsForPost(clientId, postId);
  return useMemo(() => {
    if (!platform) return null;
    return list.find((c) => c.platform === platform) ?? null;
  }, [list, platform]);
}

/** Total paid impressions delivered against a post — the latest
 *  performance snapshots of its live AND finished campaigns. 0 when the
 *  post was never boosted. Both source queries are one cache entry per
 *  client, so per-card use costs no extra requests. */
export function usePaidImpressionsForPost(
  clientId: string | undefined,
  postId: string | undefined
): number {
  const { data: live } = useLiveCampaigns(clientId);
  const { data: past } = usePastCampaigns(clientId);
  return useMemo(() => {
    if (!postId) return 0;
    return [...(live ?? []), ...(past ?? [])]
      .filter((c) => c.postId === postId)
      .reduce((sum, c) => sum + (c.performance?.impressions ?? 0), 0);
  }, [live, past, postId]);
}

export function useInvalidateLiveCampaigns() {
  const qc = useQueryClient();
  return (clientId: string) => {
    qc.invalidateQueries({ queryKey: adsQueryKeys.liveCampaigns(clientId) });
  };
}
